// Publicar e chamar a API de um alvo, num modal aberto pelos canais no topo da página do alvo.
import React, { useState } from "react";
import { ArrowRight, Rocket, RotateCcw } from "lucide-react";
import { timeAgo, useQuery } from "../lib";
import { Badge, Button, CopyButton, Dot, Modal, Seg, channelTone } from "../ui";
import { useDeploy } from "./deploy";
import type { TargetData } from "./target";

const CHANNEL_NAME: Record<string, string> = { production: "Produção", staging: "Staging" };

export function DeployModal({ p, t, data, onClose }: { p: string; t: string; data: TargetData; onClose: () => void }) {
  const channels = data.meta.channels as Record<string, number>;
  const { data: keys } = useQuery<any>("/api/keys");
  const [lang, setLang] = useState<"curl" | "js" | "python">("curl");
  const [pick, setPick] = useState<Record<string, string>>({});
  const [history, setHistory] = useState(false);
  const { deploy, dialog } = useDeploy(p, t);
  const spec = data.draft.spec;
  const url = location.origin + "/v1/" + p + "/" + t;
  const order = ["production", "staging", ...Object.keys(channels).filter((c) => c !== "production" && c !== "staging")];
  const snippets = buildSnippets(url, keys?.local ?? "ak_...", { inputs: exampleInputs(p, t, spec), ...(spec.kind === "agent" && spec.mode === "stateful" ? { session: "cliente-42" } : {}) });

  return (
    <>
      <Modal open onClose={onClose} title="Publicar" width={640}>
        {!data.versions.length && <div className="banner info"><span className="grow">Salve uma versão no editor para poder publicar.</span></div>}
        <div className="deploy-channels">
          {order.map((c) => {
            const v = channels[c];
            const last = data.deployments.find((d: any) => d.channel === c);
            const chosen = pick[c] ?? String(data.versions[0]?.n ?? "");
            return (
              <div key={c} className="deploy-channel">
                <div className="row" style={{ gap: 8, minWidth: 0 }}>
                  <Dot tone={channelTone(c) as any} />
                  <b>{CHANNEL_NAME[c] ?? c}</b>
                  <span className="faint" style={{ fontSize: 12 }}>{v ? "v" + v + (last ? " · " + timeAgo(last.created_at) : "") : "vazio"}</span>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {c === "production" && last?.previous ? (
                    <Button size="xs" variant="ghost" icon={<RotateCcw size={12} />} title={"Voltar para v" + last.previous} onClick={() => deploy("production", last.previous)}>v{last.previous}</Button>
                  ) : null}
                  {c === "staging" && v && v !== channels.production ? (
                    <Button size="xs" variant="ghost" icon={<ArrowRight size={12} />} onClick={() => deploy("production", v)}>produção</Button>
                  ) : null}
                  {data.versions.length > 0 && (
                    <>
                      <select className="select select-sm" style={{ width: 92 }} value={chosen} onChange={(e) => setPick({ ...pick, [c]: e.target.value })}>
                        {data.versions.map((x) => <option key={x.n} value={x.n} title={x.message}>v{x.n}</option>)}
                      </select>
                      <Button size="sm" variant={c === "production" ? "green" : undefined} icon={<Rocket size={13} />} disabled={!chosen || Number(chosen) === v} onClick={() => deploy(c, Number(chosen))}>Publicar</Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="code-card">
          <div className="code-card-head">
            <Seg value={lang} onChange={setLang} options={[{ value: "curl", label: "curl" }, { value: "js", label: "JavaScript" }, { value: "python", label: "Python" }]} />
            <CopyButton text={snippets[lang]} label="Copiar" />
          </div>
          <pre>{snippets[lang]}</pre>
        </div>
        <div className="field-hint" style={{ marginTop: -6 }}>
          Chama produção. Para outro canal, termine o endereço com <span className="mono">@staging</span>. A troca de versão vale na próxima chamada.
          {spec.kind === "agent" && <> Agentes podem demorar: use <span className="mono">?stream=1</span> ou <span className="mono">?async=1</span>.</>}
        </div>

        {data.deployments.length > 0 && (
          <div>
            <Button size="xs" variant="ghost" onClick={() => setHistory(!history)}>{history ? "Esconder histórico" : "Histórico (" + data.deployments.length + ")"}</Button>
            {history && (
              <div className="deploy-history">
                {data.deployments.slice(0, 12).map((d: any) => (
                  <div key={d.id} className="row" style={{ gap: 8, fontSize: 12 }}>
                    <span className="faint mono" style={{ width: 90 }}>{timeAgo(d.created_at)}</span>
                    <Badge tone={channelTone(d.channel) as any}>{d.channel}</Badge>
                    <span className="mono">{d.previous ? "v" + d.previous + " → " : ""}v{d.version}</span>
                    {d.forced ? <Badge tone="red">sem evidência</Badge> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
      {dialog}
    </>
  );
}

function exampleInputs(p: string, t: string, spec: any) {
  let stored: any = {};
  try {
    stored = JSON.parse(localStorage.getItem("atelie:inputs:" + p + "/" + t) ?? "{}");
  } catch {}
  const out: Record<string, any> = {};
  for (const [n, d] of Object.entries<any>(spec.variables ?? {})) {
    const v = stored[n];
    out[n] = d.type === "image" ? "https://exemplo.com/imagem.png" : typeof v === "string" && v.length < 160 ? v : d.example ?? "<" + n + ">";
  }
  return out;
}

function buildSnippets(url: string, key: string, body: any) {
  const NL = String.fromCharCode(10);
  const BS = String.fromCharCode(92);
  const json = JSON.stringify(body);
  const curl = ["curl " + url + " " + BS, '  -H "Authorization: Bearer ' + key + '" ' + BS, '  -H "content-type: application/json" ' + BS, "  -d '" + json + "'"].join(NL);
  const js = [
    'const res = await fetch("' + url + '", {',
    '  method: "POST",',
    '  headers: { Authorization: "Bearer ' + key + '", "content-type": "application/json" },',
    "  body: JSON.stringify(" + JSON.stringify(body, null, 2).split(NL).join(NL + "  ") + "),",
    "});",
    "const { output, version, cost_usd } = await res.json();",
  ].join(NL);
  const py = [
    "import requests",
    "",
    "res = requests.post(",
    '    "' + url + '",',
    '    headers={"Authorization": "Bearer ' + key + '"},',
    "    json=" + JSON.stringify(body, null, 4).split(NL).join(NL + "    ") + ",",
    ")",
    'print(res.json()["output"])',
  ].join(NL);
  return { curl, js, python: py };
}
